import { useLayoutEffect, useReducer, useRef } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AiBarChart } from "../charts-and-tables/AiBarChart";
import { AiLineChart } from "../charts-and-tables/AiLineChart";
import { AiPieChart } from "../charts-and-tables/AiPieChart";
import { AiTable } from "../charts-and-tables/AiTable";
import { ChartTextExportPlaceholder } from "./ChartTextExportPlaceholder";
import {
  extractFirstMatchingToolCallPayload,
  extractTextFromMessageContent,
  parseBarChartFromToolArgs,
  parseLineChartFromToolArgs,
  parsePieChartFromToolArgs,
  parseTableFromToolArgs,
} from "../ai-chat-message-utils";
import type {
  AgentMessage,
  ExportFormat,
  TableSortState,
} from "../ai-chat-message-utils";

interface ExportMessageContentProps {
  message: Extract<AgentMessage, { role: "assistant" }>;
  roleLabel: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  format: ExportFormat;
  tableSort?: TableSortState;
  onTableSortChange: (sort: TableSortState) => void;
}

type OverflowAction = { type: "setOverflowing"; isOverflowing: boolean };

const overflowReducer = (_state: boolean, action: OverflowAction) =>
  action.isOverflowing;

export const ExportMessageContent = ({
  message,
  roleLabel,
  isExpanded,
  onToggleExpand,
  format,
  tableSort,
  onTableSortChange,
}: ExportMessageContentProps) => {
  const intl = useIntl();
  const messageText = extractTextFromMessageContent(message.content);
  const toolCalls = message.toolCalls;
  const markdownRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, dispatchOverflow] = useReducer(overflowReducer, false);

  useLayoutEffect(() => {
    const element = markdownRef.current;
    if (!element) return;
    if (isExpanded) return;

    const measure = () =>
      dispatchOverflow({
        type: "setOverflowing",
        isOverflowing: element.scrollHeight > element.clientHeight,
      });

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [messageText, isExpanded]);

  const barChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseBarChartFromToolArgs,
  );
  const pieChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parsePieChartFromToolArgs,
  );
  const lineChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseLineChartFromToolArgs,
  );
  const tablePayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseTableFromToolArgs,
  );
  const isTextFormat = format === "txt";
  const chartEmptyStateText = intl.formatMessage({
    id: "app.toolboxChat.exportConversation.chartNotIncludedInText",
  });

  return (
    <div className="tcw-message tcw-message--assistant">
      <div className="ecp__card ecp__card--assistant">
        <div className="ecp__message-column">
          <p className="ecp__role-label">{roleLabel}</p>
          {barChartPayload &&
            (isTextFormat ? (
              <ChartTextExportPlaceholder text={chartEmptyStateText} />
            ) : (
              <AiBarChart payload={barChartPayload} readOnly />
            ))}
          {pieChartPayload &&
            (isTextFormat ? (
              <ChartTextExportPlaceholder text={chartEmptyStateText} />
            ) : (
              <AiPieChart payload={pieChartPayload} readOnly />
            ))}
          {lineChartPayload &&
            (isTextFormat ? (
              <ChartTextExportPlaceholder text={chartEmptyStateText} />
            ) : (
              <AiLineChart payload={lineChartPayload} readOnly />
            ))}
          {tablePayload && (
            <AiTable
              payload={tablePayload}
              readOnly
              sort={tableSort}
              onSortChange={onTableSortChange}
            />
          )}
          {messageText && (
            <>
              <div
                ref={markdownRef}
                className={`tcw-markdown ecp__markdown${
                  isExpanded ? " ecp__markdown--expanded" : ""
                }`}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{messageText}</ReactMarkdown>
              </div>
              {isOverflowing && (
                <WmButton
                  buttonType="textonly"
                  className="ecp__row-toggle ecp__row-toggle--excluded"
                  onClick={onToggleExpand}
                >
                  {intl.formatMessage({
                    id: isExpanded
                      ? "app.toolboxChat.exportConversation.hideFullResponse"
                      : "app.toolboxChat.exportConversation.showFullResponse",
                  })}
                </WmButton>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
